import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, apiErr } from '@/lib/api';
import { toast } from 'sonner';
import { KeyRound, Lock } from 'lucide-react';

/** NIP setup dialog — mostrado la primera vez tras iniciar sesión */
export function NipSetupDialog({ open, onOpenChange, onDone }) {
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) { setPin(''); setConfirm(''); setError(''); }
  }, [open]);

  const submit = async () => {
    setError('');
    if (!pin || pin.length < 4) { setError('El NIP debe tener al menos 4 dígitos'); return; }
    if (pin !== confirm) { setError('Los NIP no coinciden'); return; }
    setLoading(true);
    try {
      await api.post('/auth/nip/setup', { pin, confirm });
      toast.success('NIP creado');
      onDone?.();
      onOpenChange(false);
    } catch (e) { setError(apiErr(e)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="nip-setup-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><KeyRound className="h-5 w-5 text-primary"/> Crear NIP de seguridad</DialogTitle>
          <DialogDescription>
            Se usará para modificar periodos ya declarados ante el SAT. Elige de 4 a 10 dígitos.
            Se comportará como una contraseña (texto oculto).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">NIP</Label>
            <Input type="password" inputMode="numeric" pattern="[0-9]*" maxLength={10} data-testid="nip-setup-pin" className="h-12 text-center font-mono-num text-2xl tracking-[0.4em]" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} autoFocus/>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Confirmar NIP</Label>
            <Input type="password" inputMode="numeric" pattern="[0-9]*" maxLength={10} data-testid="nip-setup-confirm" className="h-12 text-center font-mono-num text-2xl tracking-[0.4em]" value={confirm} onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ''))}/>
          </div>
          {error && <p className="text-sm text-destructive" data-testid="nip-setup-error">{error}</p>}
          <Button data-testid="nip-setup-submit" onClick={submit} disabled={loading || !pin || !confirm} className="w-full h-11 rounded-full">
            {loading ? 'Guardando…' : 'Crear NIP'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** NIP verify modal — se abre automáticamente al detectar 428 */
export function NipVerifyDialog({ open, onOpenChange, onSuccess }) {
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (!open) { setPin(''); setError(''); } }, [open]);

  const submit = async () => {
    setError(''); setLoading(true);
    try {
      await api.post('/auth/nip/verify', { pin });
      toast.success('NIP verificado. Puedes modificar el periodo por 15 minutos.');
      onSuccess?.();
      onOpenChange(false);
    } catch (e) { setError(apiErr(e)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="nip-verify-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Lock className="h-5 w-5 text-primary"/> NIP requerido</DialogTitle>
          <DialogDescription>Este periodo ya fue presentado ante el SAT. Ingresa tu NIP para desbloquear las modificaciones por 15 minutos.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Input type="password" inputMode="numeric" pattern="[0-9]*" maxLength={10} data-testid="nip-verify-pin" className="h-14 text-center font-mono-num text-2xl tracking-[0.5em]" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} autoFocus/>
          {error && <p className="text-sm text-destructive" data-testid="nip-verify-error">{error}</p>}
          <Button data-testid="nip-verify-submit" onClick={submit} disabled={loading || !pin} className="w-full h-11 rounded-full">
            {loading ? 'Verificando…' : 'Verificar NIP'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Cambio de NIP — requiere NIP actual + 2FA (o contraseña) */
export function NipChangeDialog({ open, onOpenChange, twoFAEnabled }) {
  const [f, setF] = useState({ new_pin: '', confirm: '', code: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (!open) { setF({ new_pin: '', confirm: '', code: '', password: '' }); setError(''); } }, [open]);

  const submit = async () => {
    setError('');
    if (!f.new_pin || f.new_pin.length < 4) { setError('El nuevo NIP debe tener al menos 4 dígitos'); return; }
    if (f.new_pin !== f.confirm) { setError('Los NIP nuevos no coinciden'); return; }
    setLoading(true);
    try {
      const payload = {
        new_pin: f.new_pin,
        confirm: f.confirm,
      };
      if (twoFAEnabled) payload.code = f.code;
      else payload.password = f.password;
      await api.post('/auth/nip/change', payload);
      toast.success('NIP actualizado');
      onOpenChange(false);
    } catch (e) { setError(apiErr(e)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="nip-change-dialog" className="max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><KeyRound className="h-5 w-5 text-primary"/> Cambiar NIP</DialogTitle>
          <DialogDescription>Requiere {twoFAEnabled ? 'un código 2FA vigente' : 'tu contraseña'}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {twoFAEnabled ? (
            <Input inputMode="numeric" maxLength={6} placeholder="Código 2FA" data-testid="nip-change-code" className="h-11 text-center font-mono-num tracking-[0.4em]" value={f.code} onChange={(e) => setF(s => ({ ...s, code: e.target.value.replace(/\D/g, '') }))}/>
          ) : (
            <Input type="password" placeholder="Contraseña actual" data-testid="nip-change-password" className="h-11" value={f.password} onChange={(e) => setF(s => ({ ...s, password: e.target.value }))}/>
          )}
          <Input type="password" inputMode="numeric" pattern="[0-9]*" maxLength={10} placeholder="Nuevo NIP" data-testid="nip-change-new" className="h-11 text-center font-mono-num tracking-[0.3em]" value={f.new_pin} onChange={(e) => setF(s => ({ ...s, new_pin: e.target.value.replace(/\D/g, '') }))}/>
          <Input type="password" inputMode="numeric" pattern="[0-9]*" maxLength={10} placeholder="Confirmar nuevo NIP" data-testid="nip-change-confirm" className="h-11 text-center font-mono-num tracking-[0.3em]" value={f.confirm} onChange={(e) => setF(s => ({ ...s, confirm: e.target.value.replace(/\D/g, '') }))}/>
          {error && <p className="text-sm text-destructive" data-testid="nip-change-error">{error}</p>}
          <Button data-testid="nip-change-submit" onClick={submit} disabled={loading} className="w-full h-11 rounded-full">
            {loading ? 'Guardando…' : 'Actualizar NIP'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
